import './RejectRequestBottomSheet.scss';

import React, {
  memo,
  useState,
} from 'react';

import {
  Button,
  Input,
  message,
  Typography,
} from 'antd';
import { PiArrowBendUpRight } from 'react-icons/pi';
import { BottomSheet } from 'react-spring-bottom-sheet';

import {
  useUpdateTQServiceRequestResponse,
} from '../../../../hooks/mutations/tqConnectMutations/service-requests';
import { dynamicItemTitle } from '../../../../shared/utils';
import {
  ITQServiceRequest,
  ITQServiceRequestResponse,
} from '../../../../types/tqConnectTypes/service-requests';

interface Props {
  serviceRequestId: string;
  privateResponse?: ITQServiceRequestResponse;
  serviceRequestDetails: ITQServiceRequest;
}

const AcceptOrRejectPrivateServiceRequest: React.FC<Props> = ({
  serviceRequestId,
  privateResponse,
}) => {
  const { updateServiceRequestResponse, isPending: isAcceptingOrRejecting } =
    useUpdateTQServiceRequestResponse(
      serviceRequestId,
      privateResponse?._id || '',
    );

  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const hideRejectModal = () => {
    setIsRejectModalOpen(false);
    setRejectionReason('');
  };

  const acceptOrRejectPrivateServiceRequest = (
    status: 'accepted' | 'rejected',
  ) => {
    updateServiceRequestResponse(
      {
        serviceRequestId,
        serviceRequestResponseId: privateResponse?._id || '',
        data: {
          status,
          ...(status === 'rejected' && {
            rejectionReason: rejectionReason.trim(),
          }),
        },
      },
      {
        onSuccess: () => {
          message.success(`Request ${status}`);
          hideRejectModal();
        },
      },
    );
  };

  if (!privateResponse) {
    return null;
  }

  return (
    <>
      <div className="private-request__action">
        <div className="action__info">
          <PiArrowBendUpRight size={20} />
          Respond within a day to make sure you don’t miss the chance to help.
        </div>
        <div className="action__buttons">
          <Button
            className="siteBtn accentBtnV1 danger outlineBtn"
            danger
            disabled={isAcceptingOrRejecting}
            onClick={() => setIsRejectModalOpen(true)}
            block>
            Reject
          </Button>

          <Button
            className="siteBtn accentBtnV1 successBtn"
            loading={isAcceptingOrRejecting}
            disabled={isAcceptingOrRejecting}
            onClick={() => acceptOrRejectPrivateServiceRequest('accepted')}
            block>
            Accept
          </Button>
        </div>
      </div>

      <BottomSheet
        open={isRejectModalOpen}
        onDismiss={hideRejectModal}
        className="reject-request-bottom-sheet"
        header={
          <Typography.Title
            level={4}
            className="reject-request-bottom-sheet__title">
            Reject This Request?
          </Typography.Title>
        }>
        <div className="reject-request-bottom-sheet__content">
          <Typography.Paragraph className="reject-request-bottom-sheet__text">
            You&apos;re about to reject this{' '}
            {dynamicItemTitle('tq-offering-request')}. Please provide a reason
            so we can keep things transparent and help improve future
            experiences.
          </Typography.Paragraph>

          <Typography.Text
            strong
            className="reject-request-bottom-sheet__label">
            Reason for Rejection
          </Typography.Text>
          <Input.TextArea
            placeholder="Please share reason for rejection"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            className="reject-request-bottom-sheet__textarea"
            rows={4}
            autoSize={{ minRows: 4, maxRows: 8 }}
          />

          <div className="reject-request-bottom-sheet__footer">
            <Button
              className="siteBtn accentBtnV1 danger reject-request-bottom-sheet__reject-btn"
              disabled={!rejectionReason.trim() || isAcceptingOrRejecting}
              loading={isAcceptingOrRejecting}
              onClick={() => acceptOrRejectPrivateServiceRequest('rejected')}
              block>
              {isAcceptingOrRejecting ? 'Rejecting...' : 'Reject Request'}
            </Button>
            <Button
              className="siteBtn grayBtn outlineBtn"
              disabled={isAcceptingOrRejecting}
              onClick={hideRejectModal}
              block>
              Cancel
            </Button>
          </div>
        </div>
      </BottomSheet>
    </>
  );
};

export default memo(AcceptOrRejectPrivateServiceRequest);
